import {
  Box,
  Checkbox,
  Chip,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateEvent } from "../../Redux/slices/addEnquirySlice";
import { align_column, VM_Colors } from "../../Styles/VMStyles";
import MUITypography from "../../Components/MUITypography";
import axios from "axios";
import {
  normalizeBookingForArray,
  returnDate,
  syncSubVenueWithBookingFor,
  toDateKey,
} from "../../Util/UtilHelper";
import { API_CHECK_AVAILABILITY } from "../../Util/URLConstants";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_COUNTRY_CODE_KEY, SESSION_USER_ID_KEY } from "../../Util/VMConstants";
import Loader from "../../Components/Loader";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import StyledInputField from "../../Components/Custom/StyledInputField";
import EventTypeSelction from "./EventTypeSelction";
import dayjs from "dayjs";
import { convertToWords } from "../../Util/NumberToWord";

const booking_options = ["Breakfast", "Lunch", "Dinner", "Whole Day"];
const specialOptions = ["Breakfast", "Lunch", "Dinner"];

function AEEventDetails({ tabIndex, arrived }) {
  const [subVenues, setSubVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const events = useSelector((state) => state.addEnquiry.events);
  const customerDetails = useSelector((state) => state.addEnquiry.customer);
  const eventDetails = events[tabIndex]?.eventDetails || {};

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
    const countryCode = sessionFactory.get(SESSION_USER_COUNTRY_CODE_KEY);

  const handleChange = (field, value) => {
    let updatedDetails = { ...eventDetails, [field]: value };
    if (field === "booking_for") {
      updatedDetails = syncSubVenueWithBookingFor(updatedDetails);
    }
    const updatedEvent = { ...events[tabIndex], eventDetails: updatedDetails };
    dispatch(updateEvent({ index: tabIndex, data: updatedEvent }));
    if (field === "event_date") {
      fetchAvailableSubVenues(value);
    }
  };

  const handleChangeMultiple = (fields) => {
    const updatedDetails = { ...eventDetails, ...fields };
    const updatedEvent = { ...events[tabIndex], eventDetails: updatedDetails };
    dispatch(updateEvent({ index: tabIndex, data: updatedEvent }));
  };

  const fetchAvailableSubVenues = useCallback((eventDate) => {
    if (!eventDate) return;
    setLoading(true);
    const formattedDate = dayjs(eventDate).format("DD-MMM-YYYY");

    axios
      .get(`${API_CHECK_AVAILABILITY}?user_id=${userID}&date=${formattedDate}`)
      .then((resp) => {
        setLoading(false);
        const res = resp.data;
        if (res.status_code === 200) {
          setSubVenues(Array.isArray(res.data) ? res.data : []);
        } else {
          setSubVenues([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching sub-venue availability:", err);
        setLoading(false);
        setSubVenues([]);
      });
  }, [userID]);

  // ✅ FIX: Auto-fetch subVenues if event_date is prefilled
  useEffect(() => {
    if (eventDetails.event_date) {
      fetchAvailableSubVenues(eventDetails.event_date);
    }
  }, [eventDetails.event_date, fetchAvailableSubVenues]);

  const eventDateOptions = Array.from(
    new Set(
      [...(customerDetails?.appointment_date ?? []), eventDetails.event_date]
        .map(toDateKey)
        .filter(Boolean)
    )
  );
  const selectedEventDate = toDateKey(eventDetails.event_date);
  const currentBookingFor = normalizeBookingForArray(eventDetails.booking_for);
  const venueFromList = (subVenues ?? []).find(
    (item) => String(item.sub_venue_id) === String(eventDetails.sub_venue_id)
  );
  const availabilityBooked = normalizeBookingForArray(
    venueFromList?.event_booking_for
  );
  // Exclude this event's own slots so they stay selectable on update
  const bookedSlots = availabilityBooked.filter(
    (slot) => !currentBookingFor.includes(slot)
  );

  return (
    <Box>
      <Box sx={align_column}>
        <MuiTypographyMandatory variant='h4' label={`Select Event ${tabIndex + 1} Date`} required  sx={{fontWeight:400}}/>
        <Select
          name="event_date"
          value={selectedEventDate || ""}
          onChange={(e) => handleChange("event_date", e.target.value)}
          sx={{ mb: 2, backgroundColor: VM_Colors.light_gray }}
          displayEmpty
        >
          <MenuItem value="" disabled>  -- Select --   </MenuItem>
          {eventDateOptions.map((date) => (
            <MenuItem key={date} value={date}>
              {returnDate(date)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <MuiTypographyMandatory label="Select Event Type" variant='h3' required   />
      <Paper sx={{ backgroundColor: VM_Colors.white2, padding: 2, marginTop: 1 }}>
        <EventTypeSelction
          handleChange={handleChange}
          handleChangeMultiple={handleChangeMultiple}
          event_name={eventDetails.event_name}
          event_type={eventDetails.event_type}
        />
      </Paper>

      <Paper sx={{ backgroundColor: VM_Colors.white2, padding: 2, marginTop: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ minWidth: 250, ...align_column }}>
              <MuiTypographyMandatory label="Sub Venue" required />
              <Select
                name="sub_venue"
                value={eventDetails.sub_venue_id || ""}
                displayEmpty
                onChange={(event) => {
                  const selectedId = event.target.value;
                  const selected = subVenues.find(
                    (item) => item.sub_venue_id === selectedId
                  );

                  const updatedDetails = syncSubVenueWithBookingFor({
                    ...eventDetails,
                    sub_venue_id: selectedId,
                    sub_venue: selected ?? {},
                  });

                  const updatedEvent = {
                    ...events[tabIndex],
                    eventDetails: updatedDetails,
                  };

                  dispatch(updateEvent({ index: tabIndex, data: updatedEvent }));
                }}
                sx={{ backgroundColor: VM_Colors.white }}
              >
                   <MenuItem value="" disabled>  -- Select --   </MenuItem>
       
                {(subVenues ?? []).map((subvenue) => {
                  const isFullyBooked = specialOptions.every((slot) =>
                    subvenue.event_booking_for.includes(slot)
                  );

                  return (
                    <MenuItem
                      key={subvenue.sub_venue_id}
                      value={subvenue.sub_venue_id}
                      disabled={isFullyBooked}
                    >
                      {`${subvenue.sub_venue_name} - ${subvenue.sub_venue_capacity} ${
                        isFullyBooked ? "(Fully Booked)" : ""
                      }`}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ minWidth: 250, ...align_column }}>
              <MuiTypographyMandatory label="Booking For" required />
              <Select
                name="booking_for"
                id="booking_for"
                multiple
                value={Array.isArray(eventDetails.booking_for) ? eventDetails.booking_for || "" : []}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                displayEmpty
                sx={{ backgroundColor: VM_Colors.white }}
              >
                {booking_options.map((name) => {
                  // For "Whole Day", check if ANY of the three meals are booked
                  const isBooked = name === "Whole Day" 
                    ? specialOptions.some(slot => bookedSlots.includes(slot))
                    : bookedSlots.includes(name);
                  const isSelected = eventDetails.booking_for?.includes(name);

                  const handleItemClick = () => {
                    if (isBooked) return;

                    let updatedBookingFor = [...(eventDetails.booking_for || [])];

                    if (name === "Whole Day") {
                      const isWholeDaySelected = specialOptions.every((opt) =>
                        updatedBookingFor.includes(opt)
                      );

                      if (isWholeDaySelected) {
                        updatedBookingFor = updatedBookingFor.filter(
                          (item) => !specialOptions.includes(item)
                        );
                      } else {
                        specialOptions.forEach((opt) => {
                          if (
                            !updatedBookingFor.includes(opt) &&
                            !bookedSlots.includes(opt)
                          ) {
                            updatedBookingFor.push(opt);
                          }
                        });
                      }
                    } else {
                      if (isSelected) {
                        updatedBookingFor = updatedBookingFor.filter((item) => item !== name);
                      } else {
                        updatedBookingFor.push(name);
                      }
                    }

                    handleChange("booking_for", updatedBookingFor);
                  };

                  return (
                    <MenuItem
                      key={name}
                      value={name}
                      onClick={handleItemClick}
                      disabled={isBooked}
                    >
                      <Checkbox checked={isSelected} disabled={isBooked} />
                      <ListItemText primary={isBooked ? `${name} (Booked)` : name} />
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StyledInputField
              heading="No. of Guests"
              name="no_of_guests"
              required
              type="number"
              value={eventDetails.no_of_guests ?? ""}
              onChange={(e) => handleChange("no_of_guests", e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StyledInputField
              heading="Check-in Time"
              name="check_in"
              required
              type="time"
              InputLabelProps={{ shrink: true }}
              value={eventDetails.check_in ?? ""}
              onChange={(e) => handleChange("check_in", e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StyledInputField
              heading="Check-out Time"
              name="check_out"
              required
              type="time"
              InputLabelProps={{ shrink: true }}
              value={eventDetails.check_out ?? ""}
              onChange={(e) => handleChange("check_out", e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Stack direction={ 'column'}>
              <StyledInputField
                heading="Rent Charges"
                name="rent_charges"
                required
                type="number"
                value={eventDetails.rent_charges ?? ""}
                onChange={(e) => handleChange("rent_charges", e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
              {
                eventDetails.rent_charges>1 && countryCode === '91'
                ?  <MUITypography variant='subtitle1' label={ convertToWords(countryCode,eventDetails.rent_charges)} sx={{color:VM_Colors.primary}} /> 
                : null
              }

            </Stack>
            
          </Grid>
        </Grid>
      </Paper>
      {loading ? <Loader loading={loading} /> : null}
    </Box>
  );
}

export default AEEventDetails;
